import type { EventDto, EventOrganizer, ItemReference } from '@/types'
import { acceptHMRUpdate, defineStore } from 'pinia'
import type { ComputedRef, Ref } from 'vue'
import { computed, ref } from 'vue'
import { resolveUnref } from '@vueuse/core'
import { createEvent, deleteEventById, getEventById, getEventsForCreator, apiPublishEvent } from '@/api'
import { useAppConfigurationStore } from '@/stores/useAppConfigurationStore'
import { useUserStore } from '@/stores/userStore'
const appConfigurationStore = useAppConfigurationStore()
const userStore = useUserStore();

export interface UseEventsStore {
    allEvents: Ref<EventDto[]>
    savedEvents: ComputedRef<EventDto[]>
    activeEvents: ComputedRef<EventDto[]>
    endedEvents: ComputedRef<EventDto[]>

    getEvent(id: number): Promise<EventDto>
    deleteEvent(ev: EventDto): Promise<void>
    saveNewEvent(ev: EventDto, image?: File): Promise<EventDto>
    duplicateEvent(ev: EventDto): Promise<void>
    publishEvent(ev: EventDto): Promise<void>
    getAllEventsForOrganizers(organizers: ItemReference[]): Promise<void>
}

export const useEventStore = defineStore<'eventStore', UseEventsStore>('eventStore', function (): UseEventsStore {
    const allEvents = ref<EventDto[]>([])
    const savedEvents = computed<EventDto[]>(() => {
        return allEvents.value.filter((x) => !_eventIsPublished(x))
    })
    const activeEvents = computed<EventDto[]>(() => {
        return allEvents.value.filter((x) => _eventIsPublished(x) && !_eventIsEnded(x))
    })
    const endedEvents = computed<EventDto[]>(() => {
        return allEvents.value.filter((x) => _eventIsPublished(x) && _eventIsEnded(x))
    })

    function _eventIsPublished(ev: EventDto) {
        return ev.publish != null && ev.publish < new Date()
    }

    function _eventIsEnded(ev: EventDto) {
        return ev.end != null && ev.end < new Date()
    }
    async function getAllEventsForOrganizers(organizers: EventOrganizer[]) {

        if (allEvents.value.length === 0) {
            // remove events that are not created by any of the organizers
            const clearedEvents = allEvents.value.filter((ev) =>
                organizers.some((org) => org.id === (ev.creatorId && org.partitionKey === ev.partitionKey))
            )
            // add events that are created by any organizer that is not already in the list
            const newEvents = await Promise.all(
                organizers
                    .filter(
                        (org) =>
                            !clearedEvents.some((ev) => (org.id === ev.creatorId && org.partitionKey === ev.creatorPartitionKey))
                    )
                    .map(async (org) => {
                        let events = await getEventsForCreator(org.id as number, org.partitionKey)

                        if (appConfigurationStore.isTidTilTur()) {
                            events = events.filter((ev) => ev.typeId === 1000)
                        } else if (appConfigurationStore.isAffaldsindsamlingen()) {
                            events = events.filter(ev => ev.typeId >= 2000 && ev.typeId <= 2007);
                        }

                        return events
                    })
            );
            const flatEvents = newEvents.flat();
            const uniqueEvents: EventDto[] = Array.from(new Set(flatEvents.map(event => event.id)))
                .map(id => {
                    return flatEvents.find(event => event.id === id)!;
                });

            // set as the new list
            allEvents.value = uniqueEvents; // clearedEvents.concat(uniqueEvents.flat())
        }
    }
    async function getEvent(id: number) {
        var event = await getEventById(id)

        return event
    }

    async function deleteEvent(ev: EventDto) {
        await deleteEventById(ev.id, ev.partitionKey)
        allEvents.value = allEvents.value.filter((x) => !(x.id === ev.id))
    }

    async function saveNewEvent(ev: EventDto, image?: File): Promise<EventDto> {
        const newEvent = await createEvent(ev, image)
        if (!ev.useSignupInfo) {
            newEvent.signupInfo = ''
        }
        const newEventIdx = allEvents.value.findIndex((x) => newEvent.id === x.id)
        if (newEventIdx === -1) {
            allEvents.value.push(newEvent)
        } else {
            Object.assign(allEvents.value[newEventIdx], newEvent)
        }
        return newEvent
    }

    async function publishEvent(ev: EventDto){
        await apiPublishEvent(ev, userStore.user.id, userStore.user.email)
    }

    async function duplicateEvent(ev: EventDto) {
        const eventToDuplicate = resolveUnref(ev)
        const _id = eventToDuplicate.id
        const _publish = eventToDuplicate.publish
        eventToDuplicate.id = 0
        eventToDuplicate.publish = undefined
        saveNewEvent(eventToDuplicate)
        eventToDuplicate.id = _id
        eventToDuplicate.publish = _publish
    }

    return {
        allEvents,
        savedEvents,
        activeEvents,
        endedEvents,
        getEvent,
        deleteEvent,
        saveNewEvent,
        duplicateEvent,
        publishEvent,
        getAllEventsForOrganizers
    }
})
// make sure to pass the right store definition, `useAuth` in this case.
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useEventStore, import.meta.hot))
}
